import "./style.scss";
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type PushNotificationType = "info" | "success" | "warning" | "error";

export interface PushNotificationPayload {
  title?: string;
  message: string;
  type?: PushNotificationType;
}

type PushNotificationItem = PushNotificationPayload & {
  id: string;
  type: PushNotificationType;
  isExiting: boolean;
};

type PushNotificationsContextValue = {
  notify: (payload: PushNotificationPayload) => void;
};

const PUSH_NOTIFICATIONS_EVENT = "crm-push-notification";
const AUTO_HIDE_MS = 3000;
const EXIT_ANIMATION_MS = 260;

const PushNotificationsContext = createContext<PushNotificationsContextValue | null>(null);

export const pushNotificationBus = {
  emit: (payload: PushNotificationPayload) => {
    window.dispatchEvent(new CustomEvent(PUSH_NOTIFICATIONS_EVENT, { detail: payload }));
  },
};

export const usePushNotifications = () => {
  const context = useContext(PushNotificationsContext);
  if (!context) {
    throw new Error("usePushNotifications must be used within PushNotificationsProvider");
  }
  return context;
};

const createNotificationId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const PushNotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [notifications, setNotifications] = useState<PushNotificationItem[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const hideTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const removeTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const expiresAtRef = useRef<Record<string, number>>({});
  const remainingMsRef = useRef<Record<string, number>>({});
  const notificationsRef = useRef<PushNotificationItem[]>([]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const clearHideTimeout = useCallback((id: string) => {
    const timeout = hideTimeoutsRef.current[id];
    if (timeout) {
      clearTimeout(timeout);
      delete hideTimeoutsRef.current[id];
    }
  }, []);

  const clearRemoveTimeout = useCallback((id: string) => {
    const timeout = removeTimeoutsRef.current[id];
    if (timeout) {
      clearTimeout(timeout);
      delete removeTimeoutsRef.current[id];
    }
  }, []);

  const removeNow = useCallback(
    (id: string) => {
      clearHideTimeout(id);
      clearRemoveTimeout(id);
      delete expiresAtRef.current[id];
      delete remainingMsRef.current[id];
      setNotifications((prev) => prev.filter((item) => item.id !== id));
    },
    [clearHideTimeout, clearRemoveTimeout],
  );

  const startExit = useCallback(
    (id: string) => {
      clearHideTimeout(id);
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, isExiting: true } : item)));
      clearRemoveTimeout(id);
      removeTimeoutsRef.current[id] = setTimeout(() => removeNow(id), EXIT_ANIMATION_MS);
    },
    [clearHideTimeout, clearRemoveTimeout, removeNow],
  );

  const scheduleAutoHide = useCallback(
    (id: string, delayMs = AUTO_HIDE_MS) => {
      clearHideTimeout(id);
      const safeDelay = Math.max(0, delayMs);
      remainingMsRef.current[id] = safeDelay;
      expiresAtRef.current[id] = Date.now() + safeDelay;
      hideTimeoutsRef.current[id] = setTimeout(() => startExit(id), safeDelay);
    },
    [clearHideTimeout, startExit],
  );

  const notify = useCallback(
    (payload: PushNotificationPayload) => {
      const nextId = createNotificationId();
      const item: PushNotificationItem = {
        id: nextId,
        title: payload.title,
        message: payload.message,
        type: payload.type ?? "info",
        isExiting: false,
      };
      setNotifications((prev) => [...prev, item]);
      if (isPaused) {
        remainingMsRef.current[nextId] = AUTO_HIDE_MS;
      } else {
        scheduleAutoHide(nextId, AUTO_HIDE_MS);
      }
    },
    [isPaused, scheduleAutoHide],
  );

  useEffect(() => {
    const handleExternalPush = (event: Event) => {
      const customEvent = event as CustomEvent<PushNotificationPayload>;
      if (!customEvent.detail?.message) return;
      notify(customEvent.detail);
    };

    window.addEventListener(PUSH_NOTIFICATIONS_EVENT, handleExternalPush);
    return () => window.removeEventListener(PUSH_NOTIFICATIONS_EVENT, handleExternalPush);
  }, [notify]);

  useEffect(() => {
    const currentNotifications = notificationsRef.current;

    if (isPaused) {
      currentNotifications.forEach((item) => {
        if (item.isExiting) return;
        const expiresAt = expiresAtRef.current[item.id];
        if (expiresAt) {
          remainingMsRef.current[item.id] = Math.max(0, expiresAt - Date.now());
        } else if (!remainingMsRef.current[item.id]) {
          remainingMsRef.current[item.id] = AUTO_HIDE_MS;
        }
        clearHideTimeout(item.id);
      });
      return;
    }

    currentNotifications.forEach((item) => {
      if (item.isExiting) return;
      if (hideTimeoutsRef.current[item.id]) return;
      const remaining = remainingMsRef.current[item.id] ?? AUTO_HIDE_MS;
      scheduleAutoHide(item.id, remaining);
    });
  }, [isPaused, scheduleAutoHide, clearHideTimeout]);

  useEffect(() => {
    return () => {
      Object.values(hideTimeoutsRef.current).forEach((timeout) => clearTimeout(timeout));
      Object.values(removeTimeoutsRef.current).forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const value = useMemo<PushNotificationsContextValue>(() => ({ notify }), [notify]);

  return (
    <PushNotificationsContext.Provider value={value}>
      {children}
      <div className="push-notifications-stack" aria-live="polite">
        {notifications.map((item) => (
          <div
            key={item.id}
            className={`push-notification push-notification-${item.type} ${item.isExiting ? "exiting" : ""}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {item.title ? <p className="push-notification-title">{item.title}</p> : null}
            <p className="push-notification-message">{item.message}</p>
          </div>
        ))}
      </div>
    </PushNotificationsContext.Provider>
  );
};

export default PushNotificationsProvider;
